import {
    collection,
    doc,
    getDoc,
    getDocs,
    setDoc,
    updateDoc,
    deleteDoc,
    query,
    where,
    onSnapshot,
    serverTimestamp,
    Timestamp,
    QueryConstraint,
    writeBatch,
    arrayUnion,
    arrayRemove,
    documentId,
    increment,
} from 'firebase/firestore';
import { db, auth } from './firebaseConfig';
import { getCurrentUser } from './authService';
import { AppData, Project, Participant, Submission, Task, ParticipantGroup, TeamMember, Invitation, ParticipantInvitation, UserProfile, Nudge, ThreadMessage, Thread } from '../types';
import { AnalysisGrid, GridColumnDef, GridCell, GridOverview } from '../types/analysisGrid.types';
import { GridSummary } from '../types/dashboard.types';
import type { QuoteBankItem, AIReport, SharedReport } from '../types/deliverables.types';




/**
 * Get current user ID
 */
const getCurrentUserId = (): string => {
    const user = getCurrentUser();
    if (!user) {
        throw new Error('User not authenticated');
    }
    return user.uid;
};

/**
 * Initialize database - this is called on app start
 */
export const initializeDb = async (): Promise<void> => {

    try {
        const userId = getCurrentUserId();
        console.log('Firestore database initialized for user:', userId);
        return Promise.resolve();
    } catch (error) {
        console.error('Error initializing database:', error);
        return Promise.resolve();
    }
};

/**
 * Subscribe to real-time data changes for all user data
 * This replaces the mock pub/sub system
 *
 * @param callback - Called whenever any collection changes
 * @param orgId - Optional organization ID from JWT Claims.
 *               When provided, projects are filtered by organizationId.
 *               When null/undefined (legacy users), all projects are loaded.
 */
/**
 * ══════════════════════════════════════════════════════════════
 * TIERED DATA LOADING — Scale-optimised listener strategy
 * ══════════════════════════════════════════════════════════════
 *
 * HOT  (onSnapshot)  — teamMembers, nudges
 *   Small, high-interaction. Real-time is essential, doc count stays low.
 *
 * WARM (onSnapshot + debounce) — submissions, tasks, participants
 *   Large collections that need real-time updates, but callbacks are
 *   debounced (500 ms) so rapid-fire writes batch into single React renders.
 *
 * COLD (getDocs once + manual refresh) — projects, participantGroups,
 *   invitations, participantInvitations, users
 *   Rarely change. Fetched once on mount, re-fetched via refreshColdData().
 *
 * The external API (onDataChange callback + AppData shape) is unchanged —
 * consumers see the same interface, just backed by fewer live reads.
 * ══════════════════════════════════════════════════════════════
 */

/** Force a one-shot re-fetch of cold-tier collections.
 *  Callable from anywhere after a write to a cold collection. */
let _refreshColdData: (() => void) | null = null;
export const refreshColdData = () => { _refreshColdData?.(); };

/* Stale-while-revalidate seed. Last complete emission, keyed by scope. A
   re-subscribed listener for the SAME scope (claims refresh, join flow)
   starts from this instead of ten empty arrays — a restart's first partial
   emission used to replace good data with `projects: []`, which is the
   root cause behind most of the "patternless" screen flashes
   (docs/FLASH-AUDIT-2026-07-29.md). Cleared on sign-out. */
let _lastEmission: { key: string; data: Partial<AppData> } | null = null;
export const clearDataCache = () => { _lastEmission = null; };

export const onDataChange = (
    callback: (data: AppData) => void,
    orgId?: string | null,
    scope?: { role?: string | null; uid?: string; projectId?: string; email?: string },
): (() => void) => {

    try {
        const userId = getCurrentUserId();

        /* ── Participant scope (2026-07-28 rules patch) ──────────────
           Participants may only query their own data — org-wide queries
           are DENIED by firestore.rules. Every ref below mirrors a rule
           grant; changing one side requires changing the other. */
        const isParticipantScope = scope?.role === 'participant';
        const pProjectId = (isParticipantScope && scope?.projectId) || '';
        const pCompositeId = pProjectId && scope?.uid ? `${scope.uid}-${pProjectId}` : '';
        const pEmail = (isParticipantScope && scope?.email) || '';

        const unsubscribers: (() => void)[] = [];

        const scopeKey = [userId, orgId ?? '', scope?.role ?? '', pProjectId, pCompositeId].join('|');
        const seeded = _lastEmission?.key === scopeKey;
        const cachedData: Partial<AppData> = seeded
            ? { ..._lastEmission!.data }
            : {
                projects: [],
                participants: [],
                submissions: [],
                tasks: [],
                participantGroups: [],
                teamMembers: [],
                invitations: [],
                participantInvitations: [],
                users: [],
                nudges: [],
            };

        /* Emission gate. No emission until every registered tier has
           delivered once — the old presence check passed `[]` as "loaded",
           so the first emission was always partial (warm IndexedDB
           snapshots beat the cold projects fetch) and every boot gate
           opened against it. Consumers keep the previous appData while a
           restarted subscription refills, so waiting is flash-free; the
           seed above only stops an errored/slow tier from regressing to
           []. Each tier removes itself on first data OR error (an errored
           tier must not block boot — it stays empty/stale, as before). */
        const pendingTiers = new Set<string>();

        /* ── Debounced notify — batches rapid snapshot callbacks ── */
        let notifyTimer: ReturnType<typeof setTimeout> | null = null;
        const DEBOUNCE_MS = 500;

        const notifyCallback = () => {
            if (pendingTiers.size > 0) return;
            _lastEmission = { key: scopeKey, data: { ...cachedData } };
            // IMPORTANT: Create a new object reference so React detects the change
            callback({ ...cachedData } as AppData);
        };

        /** Immediate notify — for initial load and cold refreshes */
        const notifyImmediate = () => {
            if (notifyTimer) clearTimeout(notifyTimer);
            notifyTimer = null;
            notifyCallback();
        };

        /** Debounced notify — for warm/hot snapshot changes after initial load */
        let initialLoadComplete = false;
        const notifyDebounced = () => {
            // During initial load, fire immediately so the UI doesn't wait
            if (!initialLoadComplete) { notifyImmediate(); return; }
            if (notifyTimer) clearTimeout(notifyTimer);
            notifyTimer = setTimeout(notifyCallback, DEBOUNCE_MS);
        };

        /* ══════════ COLD TIER — fetch once, refresh on demand ══════════ */
        const orgFilter = (col: string) =>
            orgId ? query(collection(db, col), where('organizationId', '==', orgId)) : collection(db, col);

        const fetchColdDataOnce = async () => {
            if (isParticipantScope) {
                // Own project (doc read — provable against member rule),
                // own invitations by email; no groups/team-invites access.
                const [projectSnap, participantInvitationsSnap, usersSnap] = await Promise.all([
                    pProjectId ? getDoc(doc(db, 'projects', pProjectId)) : Promise.resolve(null),
                    pEmail
                        ? getDocs(query(collection(db, 'participantInvitations'), where('email', '==', pEmail)))
                        : Promise.resolve(null),
                    getDocs(query(collection(db, 'users'), where(documentId(), '==', userId))),
                ]);

                cachedData.projects = projectSnap?.exists()
                    ? [{ id: projectSnap.id, ...projectSnap.data() } as Project]
                    : [];
                cachedData.participantGroups = [];
                cachedData.invitations = [];
                cachedData.participantInvitations = participantInvitationsSnap
                    ? participantInvitationsSnap.docs.map(d => ({ id: d.id, ...d.data() } as ParticipantInvitation))
                    : [];
                cachedData.users = usersSnap.docs.map(d => ({ uid: d.id, ...d.data() } as UserProfile));

                pendingTiers.delete('cold');
                notifyImmediate();
                return;
            }

            const [
                projectsSnap,
                groupsSnap,
                invitationsSnap,
                participantInvitationsSnap,
                usersSnap,
            ] = await Promise.all([
                getDocs(orgFilter('projects')),
                getDocs(orgFilter('participantGroups')),
                getDocs(orgFilter('invitations')),
                getDocs(orgFilter('participantInvitations')),
                getDocs(
                    orgId === null
                        ? query(collection(db, 'users'), where('role', 'in', ['researcher', 'superadmin', 'participant']))
                        : query(collection(db, 'users'), where(documentId(), '==', userId))
                ),
            ]);

            cachedData.projects = projectsSnap.docs.map(d => ({ id: d.id, ...d.data() } as Project));
            cachedData.participantGroups = groupsSnap.docs.map(d => ({ id: d.id, ...d.data() } as ParticipantGroup));
            cachedData.invitations = invitationsSnap.docs.map(d => ({ id: d.id, ...d.data() } as Invitation));
            cachedData.participantInvitations = participantInvitationsSnap.docs.map(d => ({ id: d.id, ...d.data() } as ParticipantInvitation));
            cachedData.users = usersSnap.docs.map(d => ({ uid: d.id, ...d.data() } as UserProfile));

            pendingTiers.delete('cold');
            notifyImmediate();
        };

        /**
         * Cold-tier fetch with bounded retry.
         *
         * On an in-session login (vs. a cold refresh) the listener is
         * re-established during the auth transition, before the fresh JWT
         * has propagated to Firestore's channel. The first getDocs then
         * fails with permission-denied. With no retry, cachedData.projects
         * stays [] for the whole session — only a write triggers
         * refreshColdData — so the researcher dashboard splash spins
         * forever until a manual page refresh. The token settles within
         * ~1-2s, so a few backed-off retries self-heal the race.
         */
        const fetchColdData = async () => {
            const delays = [500, 1500, 3000, 5000];
            for (let attempt = 0; attempt <= delays.length; attempt++) {
                try {
                    await fetchColdDataOnce();
                    return;
                } catch (err) {
                    if (attempt === delays.length) {
                        console.error('[onDataChange] cold fetch failed after retries', err);
                        // Unblock emission — a dead cold tier must not hold
                        // the whole app on the splash (data stays empty/stale).
                        pendingTiers.delete('cold');
                        notifyImmediate();
                        return;
                    }
                    // Force a token refresh before retrying. A role change
                    // (provisionUser / joinProject) revokes the old token and
                    // stamps new claims; without this the client retries with
                    // the stale token, exhausts the ladder, and the app is dead
                    // until a manual reload.
                    try { await auth.currentUser?.getIdToken(true); } catch { /* offline — retry anyway */ }
                    console.warn(
                        `[onDataChange] cold fetch attempt ${attempt + 1} failed, retrying in ${delays[attempt]}ms`,
                        err,
                    );
                    await new Promise(r => setTimeout(r, delays[attempt]));
                }
            }
        };

        // Expose refresh handle for write-side callers
        _refreshColdData = fetchColdData;

        // Initial cold fetch
        pendingTiers.add('cold');
        fetchColdData();

        /* ══════════ WARM TIER — real-time with debounced notify ══════════ */

        // Participants — participant scope: own record only (doc listener;
        // a documentId()== query would not be provable against the rules)
        if (isParticipantScope) {
            if (pCompositeId) {
                pendingTiers.add('participants');
                unsubscribers.push(
                    onSnapshot(
                        doc(db, 'participants', pCompositeId),
                        (snap) => {
                            pendingTiers.delete('participants');
                            cachedData.participants = snap.exists()
                                ? [{ id: snap.id, ...snap.data() } as Participant]
                                : [];
                            notifyDebounced();
                        },
                        (err) => {
                            console.error('[FS_DENY:onDataChange.participants:own]', { pCompositeId, err });
                            pendingTiers.delete('participants');
                            notifyDebounced();
                        },
                    )
                );
            }
        } else {
            pendingTiers.add('participants');
            unsubscribers.push(
                onSnapshot(
                    orgFilter('participants'),
                    (snapshot) => {
                        pendingTiers.delete('participants');
                        cachedData.participants = snapshot.docs.map(d => ({ id: d.id, ...d.data() } as Participant));
                        notifyDebounced();
                    },
                    (err) => {
                        console.error('[FS_DENY:onDataChange.participants]', { orgId, err });
                        pendingTiers.delete('participants');
                        notifyDebounced();
                    },
                )
            );
        }

        // Submissions — participant scope: own only (participantId pinned)
        if (!isParticipantScope || pCompositeId) {
            pendingTiers.add('submissions');
            unsubscribers.push(
                onSnapshot(
                    isParticipantScope
                        ? query(collection(db, 'submissions'), where('participantId', '==', pCompositeId))
                        : orgFilter('submissions'),
                    (snapshot) => {
                        pendingTiers.delete('submissions');
                        cachedData.submissions = snapshot.docs.map(d => ({ id: d.id, ...d.data() } as Submission));
                        notifyDebounced();
                    },
                    (err) => {
                        console.error('[FS_DENY:onDataChange.submissions]', { orgId, err });
                        pendingTiers.delete('submissions');
                        notifyDebounced();
                    },
                )
            );
        }

        // Tasks — participant scope: own project only (projectId pinned)
        if (!isParticipantScope || pProjectId) {
            pendingTiers.add('tasks');
            unsubscribers.push(
                onSnapshot(
                    isParticipantScope
                        ? query(collection(db, 'tasks'), where('projectId', '==', pProjectId))
                        : orgFilter('tasks'),
                    (snapshot) => {
                        pendingTiers.delete('tasks');
                        cachedData.tasks = snapshot.docs.map(d => ({ id: d.id, ...d.data() } as Task));
                        notifyDebounced();
                    },
                    (err) => {
                        console.error('[FS_DENY:onDataChange.tasks]', { orgId, err });
                        pendingTiers.delete('tasks');
                        notifyDebounced();
                    },
                )
            );
        }

        /* ══════════ HOT TIER — real-time, immediate notify ══════════ */

        // Team members — participant scope: own project (presence/names UI)
        if (!isParticipantScope || pProjectId) {
            pendingTiers.add('teamMembers');
            unsubscribers.push(
                onSnapshot(
                    isParticipantScope
                        ? query(collection(db, 'teamMembers'), where('projectId', '==', pProjectId))
                        : orgFilter('teamMembers'),
                    (snapshot) => {
                        pendingTiers.delete('teamMembers');
                        cachedData.teamMembers = snapshot.docs.map(d => ({ id: d.id, ...d.data() } as TeamMember));
                        notifyDebounced();
                    },
                    (err) => {
                        console.error('[FS_DENY:onDataChange.teamMembers]', { orgId, err });
                        pendingTiers.delete('teamMembers');
                        notifyDebounced();
                    },
                )
            );
        }

        // Nudges — participant scope: own only (participantId pinned)
        if (!isParticipantScope || pCompositeId) {
            pendingTiers.add('nudges');
            unsubscribers.push(
                onSnapshot(
                    isParticipantScope
                        ? query(collection(db, 'nudges'), where('participantId', '==', pCompositeId))
                        : orgFilter('nudges'),
                    (snapshot) => {
                        pendingTiers.delete('nudges');
                        cachedData.nudges = snapshot.docs.map(d => ({ id: d.id, ...d.data() } as Nudge));
                        notifyDebounced();
                    },
                    (err) => {
                        console.error('[FS_DENY:onDataChange.nudges]', { orgId, err });
                        pendingTiers.delete('nudges');
                        notifyDebounced();
                    },
                )
            );
        }

        // Mark initial load as complete after a short window
        setTimeout(() => { initialLoadComplete = true; }, 3000);

        // Return unsubscribe function that cleans up all listeners
        return () => {
            if (notifyTimer) clearTimeout(notifyTimer);
            _refreshColdData = null;
            unsubscribers.forEach(unsub => unsub());
        };
    } catch (error) {
        console.error('Error setting up data listeners:', error);
        // Return empty unsubscribe function
        return () => { };
    }
};

/**
 * @deprecated — use targeted service functions instead. Remove once confirmed unused.
 *
 * Save entire AppData - this is used by the App component
 * In a real implementation, we'd break this down into individual operations
 */
export const saveDataToDb = async (data: AppData): Promise<void> => {
    try {
        const userId = getCurrentUserId();
        const batch = writeBatch(db);

        // Save projects
        for (const project of data.projects) {
            const projectRef = doc(db, 'projects', project.id);
            batch.set(projectRef, {
                ...project,
                ownerId: userId,
                teamMemberIds: arrayUnion(userId),
                updatedAt: serverTimestamp(),
            }, { merge: true });
        }

        // Save participants
        for (const participant of data.participants) {
            const participantRef = doc(db, 'participants', participant.id);
            batch.set(participantRef, {
                ...participant,
                createdBy: userId,
                updatedAt: serverTimestamp(),
            }, { merge: true });
        }

        // Save submissions
        for (const submission of data.submissions) {
            const submissionRef = doc(db, 'submissions', submission.id);
            batch.set(submissionRef, {
                ...submission,
                userId,
                updatedAt: serverTimestamp(),
            }, { merge: true });
        }

        // Save tasks
        for (const task of data.tasks) {
            const taskRef = doc(db, 'tasks', task.id);
            batch.set(taskRef, {
                ...task,
                createdBy: userId,
                updatedAt: serverTimestamp(),
            }, { merge: true });
        }

        // Save participant groups
        for (const group of data.participantGroups) {
            const groupRef = doc(db, 'participantGroups', group.id);
            batch.set(groupRef, {
                ...group,
                createdBy: userId,
                updatedAt: serverTimestamp(),
            }, { merge: true });
        }

        // Save team members
        for (const member of data.teamMembers) {
            const memberRef = doc(db, 'teamMembers', member.id);
            batch.set(memberRef, {
                ...member,
                updatedAt: serverTimestamp(),
            }, { merge: true });
        }

        // Save invitations
        for (const invitation of data.invitations) {
            const invitationRef = doc(db, 'invitations', invitation.id);
            batch.set(invitationRef, {
                ...invitation,
                invitedBy: userId,
                createdAt: serverTimestamp(),
            }, { merge: true });
        }

        // Save participant invitations
        for (const invitation of data.participantInvitations) {
            const invitationRef = doc(db, 'participantInvitations', invitation.id);
            batch.set(invitationRef, {
                ...invitation,
                invitedBy: userId,
            }, { merge: true });
        }

        await batch.commit();
        console.log('Data saved to Firestore successfully');
    } catch (error) {
        console.error('Error saving data to Firestore:', error);
        throw error;
    }
};

/**
 * Individual CRUD operations for more granular control
 */

// Projects
export const createProject = async (project: Omit<Project, 'id'>): Promise<string> => {

    const userId = getCurrentUserId();
    const projectRef = doc(collection(db, 'projects'));
    await setDoc(projectRef, {
        ...project,
        ownerId: userId,
        teamMemberIds: [userId],
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
    });
    refreshColdData();
    return projectRef.id;
};

export const updateProject = async (projectId: string, updates: Partial<Project>): Promise<void> => {

    const projectRef = doc(db, 'projects', projectId);
    await setDoc(projectRef, {
        ...updates,
        updatedAt: serverTimestamp(),
    }, { merge: true });
    refreshColdData();
};

export const createOrUpdateProject = async (projectId: string, project: Omit<Project, 'id'>): Promise<void> => {
    const userId = getCurrentUserId();
    const projectRef = doc(db, 'projects', projectId);
    await setDoc(projectRef, {
        ...project,
        ownerId: userId,
        teamMemberIds: arrayUnion(userId),
        updatedAt: serverTimestamp(),
    }, { merge: true });
    refreshColdData();
};

export const getProject = async (projectId: string): Promise<Project | null> => {

    const projectDoc = await getDoc(doc(db, 'projects', projectId));
    if (projectDoc.exists()) {
        return { id: projectDoc.id, ...projectDoc.data() } as Project;
    }
    return null;
};

export const deleteProject = async (projectId: string): Promise<void> => {

    await deleteDoc(doc(db, 'projects', projectId));
    refreshColdData();
};

// Participants
export const createParticipant = async (participant: Omit<Participant, 'id'>): Promise<string> => {

    const userId = getCurrentUserId();
    const participantRef = doc(collection(db, 'participants'));
    await setDoc(participantRef, {
        ...participant,
        createdBy: userId,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
    });
    return participantRef.id;
};

/**
 * Fetch every participant for a project (no pagination). Used by the
 * "Nudge All" action so it actually targets the full roster, not just
 * the 50-row page that the MissionControlView grid happens to have
 * loaded. Returns the raw docs so callers can read .id and .projectId.
 */
export const getAllProjectParticipants = async (
    projectId: string,
    organizationId: string | null,
): Promise<Participant[]> => {
    const constraints: QueryConstraint[] = [where('projectId', '==', projectId)];
    if (organizationId) constraints.push(where('organizationId', '==', organizationId));
    const snap = await getDocs(query(collection(db, 'participants'), ...constraints));
    return snap.docs.map(d => ({ id: d.id, ...d.data() } as Participant));
};

export const updateParticipant = async (participantId: string, updates: Partial<Participant>): Promise<void> => {

    const participantRef = doc(db, 'participants', participantId);
    await updateDoc(participantRef, {
        ...updates,
        updatedAt: serverTimestamp(),
    });
};

export const deleteParticipant = async (participantId: string): Promise<void> => {

    await deleteDoc(doc(db, 'participants', participantId));
};

// Submissions
// No offline fast-path: the old one diverted writes into a 'submissions_queue'
// store that nothing ever drained, so offline submissions were silently lost
// behind a "will sync when online" toast. Firestore's persistentLocalCache
// (firebaseConfig.ts) queues the write durably and replays it on reconnect.
export const createSubmission = async (submission: Omit<Submission, 'id'>): Promise<string> => {
    const userId = getCurrentUserId();
    const submissionRef = doc(collection(db, 'submissions'));

    // Ensure organizationId is set — look up from project if missing
    let orgId = submission.organizationId;
    if (!orgId && submission.projectId) {
        try {
            const projectDoc = await getDoc(doc(db, 'projects', submission.projectId));
            if (projectDoc.exists()) {
                orgId = projectDoc.data()?.organizationId || '';
            }
        } catch (error) {
            console.error('Error fetching project for organizationId:', error);
        }
    }

    // Get the task to find the creator (if taskId exists)
    let taskCreatorId: string | undefined;
    if (submission.taskId) {
        try {
            const taskDoc = await getDoc(doc(db, 'tasks', submission.taskId));
            if (taskDoc.exists()) {
                const taskData = taskDoc.data();
                taskCreatorId = taskData?.createdBy;
            }
        } catch (error) {
            console.error('Error fetching task for creator ID:', error);
        }
    }

    await setDoc(submissionRef, {
        ...submission,
        organizationId: orgId || '',
        submittedBy: userId,
        ...(taskCreatorId ? { taskCreatorId } : {}),
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
    });
    return submissionRef.id;
};

export const updateSubmission = async (submissionId: string, updates: Partial<Submission>): Promise<void> => {

    const submissionRef = doc(db, 'submissions', submissionId);
    await updateDoc(submissionRef, {
        ...updates,
        updatedAt: serverTimestamp(),
    });
};

export const batchUpdateSubmissions = async (
  updates: { id: string; data: Partial<Submission> }[]
): Promise<void> => {
  const batch = writeBatch(db);

  for (const { id, data } of updates) {
    const submissionRef = doc(db, 'submissions', id);
    batch.update(submissionRef, {
      ...data,
      updatedAt: serverTimestamp(),
    });
  }

  await batch.commit();
};

export const deleteSubmission = async (submissionId: string): Promise<void> => {

    await deleteDoc(doc(db, 'submissions', submissionId));
};

// Query Helpers for Submissions

/**
 * Get all submissions for a specific task
 */
export const getSubmissionsByTask = async (taskId: string): Promise<Submission[]> => {

    const submissionsQuery = query(
        collection(db, 'submissions'),
        where('taskId', '==', taskId)
    );
    const querySnapshot = await getDocs(submissionsQuery);
    return querySnapshot.docs
        .filter(doc => doc.exists())
        .map(doc => {
            const data = doc.data();
            return { id: doc.id, ...data } as Submission;
        });
};

/**
 * Get all submissions for tasks created by a specific researcher
 */
export const getSubmissionsByResearcher = async (researcherId: string, projectId?: string): Promise<Submission[]> => {

    let submissionsQuery;

    if (projectId) {
        // Filter by both researcher and project
        submissionsQuery = query(
            collection(db, 'submissions'),
            where('taskCreatorId', '==', researcherId),
            where('projectId', '==', projectId)
        );
    } else {
        // Filter by researcher only
        submissionsQuery = query(
            collection(db, 'submissions'),
            where('taskCreatorId', '==', researcherId)
        );
    }

    const querySnapshot = await getDocs(submissionsQuery);
    return querySnapshot.docs
        .filter(doc => doc.exists())
        .map(doc => {
            const data = doc.data();
            return { id: doc.id, ...(data as any) } as Submission;
        });
};

/**
 * Get all submissions for a specific participant
 */
export const getSubmissionsByParticipant = async (participantId: string, projectId?: string): Promise<Submission[]> => {

    let submissionsQuery;

    if (projectId) {
        submissionsQuery = query(
            collection(db, 'submissions'),
            where('participantId', '==', participantId),
            where('projectId', '==', projectId)
        );
    } else {
        submissionsQuery = query(
            collection(db, 'submissions'),
            where('participantId', '==', participantId)
        );
    }

    const querySnapshot = await getDocs(submissionsQuery);
    return querySnapshot.docs
        .filter(doc => doc.exists())
        .map(doc => {
            const data = doc.data();
            return { id: doc.id, ...(data as any) } as Submission;
        });
};


// Tasks
export const createTask = async (task: Omit<Task, 'id'>): Promise<string> => {

    const userId = getCurrentUserId();
    const taskRef = doc(collection(db, 'tasks'));
    await setDoc(taskRef, {
        ...task,
        createdBy: userId,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
    });
    return taskRef.id;
};

export const updateTask = async (taskId: string, updates: Partial<Task>): Promise<void> => {

    const taskRef = doc(db, 'tasks', taskId);
    await setDoc(taskRef, {
        ...updates,
        updatedAt: serverTimestamp(),
    }, { merge: true });
};

export const batchUpdateTasks = async (
  updates: { id: string; data: Partial<Task> }[]
): Promise<void> => {
  const batch = writeBatch(db);

  for (const { id, data } of updates) {
    const taskRef = doc(db, 'tasks', id);
    batch.update(taskRef, {
      ...data,
      updatedAt: serverTimestamp(),
    });
  }

  await batch.commit();
};

export const deleteTask = async (taskId: string): Promise<void> => {

    await deleteDoc(doc(db, 'tasks', taskId));
};

// Nudges
export const createNudge = async (nudge: Omit<Nudge, 'id'>): Promise<string> => {

    const nudgeRef = doc(collection(db, 'nudges'));
    await setDoc(nudgeRef, {
        ...nudge,
        createdAt: serverTimestamp(),
    });
    return nudgeRef.id;
};

export const updateNudge = async (nudgeId: string, updates: Partial<Nudge>): Promise<void> => {

    const nudgeRef = doc(db, 'nudges', nudgeId);
    await updateDoc(nudgeRef, {
        ...updates,
        updatedAt: serverTimestamp(),
    });
};

export const deleteNudge = async (nudgeId: string): Promise<void> => {

    await deleteDoc(doc(db, 'nudges', nudgeId));
};

/**
 * Get all nudges for a specific participant
 */
export const getNudgesByParticipant = async (participantId: string): Promise<Nudge[]> => {

    const nudgesQuery = query(
        collection(db, 'nudges'),
        where('participantId', '==', participantId)
    );
    const querySnapshot = await getDocs(nudgesQuery);
    return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Nudge));
};

/**
 * Mark a nudge as read
 */
export const markNudgeAsRead = async (nudgeId: string): Promise<void> => {

    const nudgeRef = doc(db, 'nudges', nudgeId);
    await updateDoc(nudgeRef, {
        isRead: true,
        readAt: new Date().toISOString(),
        updatedAt: serverTimestamp(),
    });
};

/**
 * Update a participant profile directly (create or update with merge)
 */
export const updateParticipantProfile = async (
  participantId: string,
  data: Partial<Participant>
): Promise<void> => {
  await setDoc(doc(db, 'participants', participantId), data, { merge: true });
};

// Team Member Invitations
export const createInvitation = async (invitationId: string, invitation: Omit<Invitation, 'id'>): Promise<void> => {
  const userId = getCurrentUserId();
  const invitationRef = doc(db, 'invitations', invitationId);
  await setDoc(invitationRef, {
    ...invitation,
    invitedBy: userId,
    createdAt: serverTimestamp(),
  });
  refreshColdData();
};

export const deleteInvitation = async (invitationId: string): Promise<void> => {
  await deleteDoc(doc(db, 'invitations', invitationId));
  refreshColdData();
};

// Participant Invitations
export const createParticipantInvitation = async (invitationId: string, invitation: Omit<ParticipantInvitation, 'id'>): Promise<void> => {
  const userId = getCurrentUserId();
  const invitationRef = doc(db, 'participantInvitations', invitationId);
  await setDoc(invitationRef, {
    ...invitation,
    invitedBy: userId,
    createdAt: serverTimestamp(),
  });
  refreshColdData();
};

export const batchCreateParticipantInvitations = async (
  invitations: { id: string; data: Omit<ParticipantInvitation, 'id'> }[]
): Promise<void> => {
  const userId = getCurrentUserId();
  const batch = writeBatch(db);

  for (const { id, data } of invitations) {
    const invitationRef = doc(db, 'participantInvitations', id);
    batch.set(invitationRef, {
      ...data,
      invitedBy: userId,
      createdAt: serverTimestamp(),
    });
  }

  await batch.commit();
  refreshColdData();
};

export const deleteParticipantInvitation = async (invitationId: string): Promise<void> => {
  await deleteDoc(doc(db, 'participantInvitations', invitationId));
  refreshColdData();
};

// Team Members
export const deleteTeamMember = async (memberId: string): Promise<void> => {
  await deleteDoc(doc(db, 'teamMembers', memberId));
};

export const updateTeamMemberRole = async (memberId: string, role: TeamMember['role']): Promise<void> => {
  const memberRef = doc(db, 'teamMembers', memberId);
  await updateDoc(memberRef, {
    role,
    updatedAt: serverTimestamp(),
  });
};

export const updateTeamMemberActivity = async (memberId: string, projectId?: string): Promise<void> => {
  const memberRef = doc(db, 'teamMembers', memberId);
  await updateDoc(memberRef, {
    lastActiveAt: serverTimestamp(),
    ...(projectId ? { projectId } : {}),
  });
};

// Participant Groups
export const createOrUpdateGroup = async (groupId: string, group: Omit<ParticipantGroup, 'id'>): Promise<void> => {
  const userId = getCurrentUserId();
  const groupRef = doc(db, 'participantGroups', groupId);
  await setDoc(groupRef, {
    ...group,
    createdBy: userId,
    updatedAt: serverTimestamp(),
  }, { merge: true });
  refreshColdData();
};

export const deleteGroup = async (groupId: string): Promise<void> => {
  await deleteDoc(doc(db, 'participantGroups', groupId));
  refreshColdData();
};

// Admin Audit Log
export const writeAuditLogEntry = async (entry: {
  action: string;
  performedBy: string;
  targetUid?: string;
  targetOrgId?: string;
  role?: string;
  organizationId?: string | null;
}): Promise<string> => {
  const logRef = doc(collection(db, 'adminAuditLog'));
  await setDoc(logRef, {
    ...entry,
    timestamp: serverTimestamp(),
  });
  return logRef.id;
};

// Organizations
export const createOrganization = async (data: {
  name: string;
  createdBy: string;
  billingEmail?: string;
  maxSeats?: number;
}): Promise<string> => {
  const orgRef = doc(collection(db, 'organizations'));
  await setDoc(orgRef, {
    name: data.name,
    status: 'active',
    createdAt: serverTimestamp(),
    createdBy: data.createdBy,
    ...(data.billingEmail ? { billingEmail: data.billingEmail } : {}),
    ...(data.maxSeats ? { maxSeats: data.maxSeats } : {}),
  });
  return orgRef.id;
};

// Thread Messages (per-submission conversation)
export const createThreadMessage = async (
    message: Omit<ThreadMessage, 'id'>,
    taskName?: string,
): Promise<string> => {
    const batch = writeBatch(db);

    // 1. Thread message
    const msgRef = doc(collection(db, 'threadMessages'));
    batch.set(msgRef, {
        ...message,
        createdAt: serverTimestamp(),
    });

    // 2. Upsert thread summary doc (id = submissionId)
    const threadRef = doc(db, 'threads', message.submissionId);
    const recipientField = message.senderRole === 'researcher'
        ? 'participantUnread'
        : 'researcherUnread';

    batch.set(threadRef, {
        organizationId: message.organizationId,
        projectId: message.projectId,
        participantId: message.participantId,
        submissionId: message.submissionId,
        ...(taskName ? { taskName } : {}),
        lastMessageBody: message.body,
        lastMessageAt: serverTimestamp(),
        lastMessageSenderRole: message.senderRole,
        lastMessageSenderName: message.senderName,
        [recipientField]: increment(1),
        updatedAt: serverTimestamp(),
    }, { merge: true });

    await batch.commit();
    return msgRef.id;
};

export const markThreadMessageRead = async (messageId: string): Promise<void> => {
    const msgRef = doc(db, 'threadMessages', messageId);
    await updateDoc(msgRef, {
        readByRecipient: true,
        readAt: new Date().toISOString(),
    });
};

export const batchMarkThreadMessagesRead = async (messageIds: string[]): Promise<void> => {
    if (messageIds.length === 0) return;
    const batch = writeBatch(db);
    const now = new Date().toISOString();
    for (const id of messageIds) {
        batch.update(doc(db, 'threadMessages', id), { readByRecipient: true, readAt: now });
    }
    await batch.commit();
};

/** Reset the unread counter on a thread doc for a given role */
export const markThreadReadForRole = async (
    submissionId: string,
    role: 'researcher' | 'participant',
): Promise<void> => {
    const threadRef = doc(db, 'threads', submissionId);
    const field = role === 'participant' ? 'participantUnread' : 'researcherUnread';
    await updateDoc(threadRef, { [field]: 0, updatedAt: serverTimestamp() }).catch(() => {
        // Thread doc may not exist yet — safe to ignore
    });
};

/**
 * Request a redo on a submission — batched write:
 * 1. Creates a redo_request ThreadMessage
 * 2. Sets redoRequestedAt/By on the Submission
 */
export const requestRedo = async (params: {
    submissionId: string;
    organizationId: string;
    projectId: string;
    participantId: string;
    researcherUid: string;
    researcherName: string;
    reason: string;
    taskName?: string;
}): Promise<void> => {
    const batch = writeBatch(db);

    // 1. Thread message
    const msgRef = doc(collection(db, 'threadMessages'));
    batch.set(msgRef, {
        organizationId: params.organizationId,
        projectId: params.projectId,
        submissionId: params.submissionId,
        participantId: params.participantId,
        senderUid: params.researcherUid,
        senderRole: 'researcher',
        senderName: params.researcherName,
        body: params.reason,
        createdAt: serverTimestamp(),
        readByRecipient: false,
        messageType: 'redo_request',
    });

    // 2. Submission update
    const subRef = doc(db, 'submissions', params.submissionId);
    batch.update(subRef, {
        redoRequestedAt: serverTimestamp(),
        redoRequestedBy: params.researcherUid,
    });

    // 3. Thread summary doc
    const threadRef = doc(db, 'threads', params.submissionId);
    batch.set(threadRef, {
        organizationId: params.organizationId,
        projectId: params.projectId,
        participantId: params.participantId,
        submissionId: params.submissionId,
        ...(params.taskName ? { taskName: params.taskName } : {}),
        lastMessageBody: params.reason,
        lastMessageAt: serverTimestamp(),
        lastMessageSenderRole: 'researcher',
        lastMessageSenderName: params.researcherName,
        participantUnread: increment(1),
        updatedAt: serverTimestamp(),
    }, { merge: true });

    await batch.commit();
};

/**
 * Resolve/unresolve a thread on a submission.
 */
export const resolveThread = async (
    submissionId: string,
    researcherUid: string | null,
): Promise<void> => {
    const subRef = doc(db, 'submissions', submissionId);
    if (researcherUid) {
        await updateDoc(subRef, {
            threadResolvedAt: serverTimestamp(),
            threadResolvedBy: researcherUid,
        });
    } else {
        await updateDoc(subRef, {
            threadResolvedAt: null,
            threadResolvedBy: null,
        });
    }
};

/* ══════════════════════════════════════════════════════════════
   PHASE 3 — Multi-Region config fetch
   Retrieves the regional storage bucket and Firestore DB for an org.
   ══════════════════════════════════════════════════════════════ */
export const getOrgRegionConfig = async (
    organizationId: string,
): Promise<{ region: string; storageBucket: string; firestoreDb: string }> => {
    try {
        const { getFunctions, httpsCallable } = await import('firebase/functions');
        const functions = getFunctions();
        const getConfig = httpsCallable(functions, 'getRegionConfig');
        const result = await getConfig({ organizationId });
        return result.data as { region: string; storageBucket: string; firestoreDb: string };
    } catch (err) {
        console.warn('[getOrgRegionConfig] Failed, defaulting to US:', err);
        return { region: 'us', storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET, firestoreDb: '(default)' };
    }
};

/* ══════════════════════════════════════════════════════════════
   PHASE 5 — Client-side quota check
   Calls the checkQuota Cloud Function before creating resources.
   Returns { allowed, currentCount, limit, tier }.
   ══════════════════════════════════════════════════════════════ */
export const checkOrgQuota = async (
    organizationId: string,
    resource: 'participant' | 'project' | 'teamMember',
): Promise<{ allowed: boolean; currentCount: number; limit: number; tier: string }> => {
    try {
        const { getFunctions, httpsCallable } = await import('firebase/functions');
        const functions = getFunctions();
        const checkQuota = httpsCallable(functions, 'checkQuota');
        const result = await checkQuota({ organizationId, resource });
        return result.data as { allowed: boolean; currentCount: number; limit: number; tier: string };
    } catch (err) {
        console.warn('[checkOrgQuota] Quota check failed, allowing by default:', err);
        return { allowed: true, currentCount: 0, limit: -1, tier: 'unknown' };
    }
};

/* ══════════════════════════════════════════════════════════════
   ANALYSIS GRID — CRUD for grids, cells, and overviews
   ══════════════════════════════════════════════════════════════ */

export const fetchAnalysisGrids = async (projectId: string, orgId: string): Promise<AnalysisGrid[]> => {
    const snap = await getDocs(
        query(collection(db, 'analysisGrids'), where('projectId', '==', projectId), where('organizationId', '==', orgId))
    );
    return snap.docs.map(d => ({ id: d.id, ...d.data() } as AnalysisGrid));
};

export const fetchGridCellsAndOverviews = async (
    gridId: string,
): Promise<{ cells: Map<string, GridCell>; overviews: Map<string, GridOverview> }> => {
    const [cellsSnap, overviewsSnap] = await Promise.all([
        getDocs(query(collection(db, 'gridCells'), where('gridId', '==', gridId))),
        getDocs(query(collection(db, 'gridOverviews'), where('gridId', '==', gridId))),
    ]);

    const cells = new Map<string, GridCell>();
    cellsSnap.docs.forEach(d => {
        const data = d.data() as GridCell;
        cells.set(data.id, data);
    });

    const overviews = new Map<string, GridOverview>();
    overviewsSnap.docs.forEach(d => {
        const data = d.data() as GridOverview;
        overviews.set(data.id, data);
    });

    return { cells, overviews };
};

export const createAnalysisGrid = async (data: {
    name: string;
    projectId: string;
    organizationId: string;
    createdBy: string;
}): Promise<AnalysisGrid> => {
    const now = new Date().toISOString();
    const gridRef = doc(collection(db, 'analysisGrids'));
    const gridData = {
        name: data.name,
        projectId: data.projectId,
        organizationId: data.organizationId,
        createdBy: data.createdBy,
        columns: [],
        createdAt: now,
        updatedAt: now,
    };
    await setDoc(gridRef, gridData);
    return { id: gridRef.id, ...gridData };
};

export const updateAnalysisGridColumns = async (
    gridId: string,
    columns: GridColumnDef[],
): Promise<void> => {
    await updateDoc(doc(db, 'analysisGrids', gridId), {
        columns,
        updatedAt: new Date().toISOString(),
    });
};

export const renameAnalysisGrid = async (gridId: string, name: string): Promise<void> => {
    await updateDoc(doc(db, 'analysisGrids', gridId), {
        name,
        updatedAt: new Date().toISOString(),
    });
};

export const deleteAnalysisGrid = async (gridId: string): Promise<void> => {
    await deleteDoc(doc(db, 'analysisGrids', gridId));
};

/** Fetch the grid-level AI summary for a grid. Returns null if not generated yet. */
export const fetchGridSummary = async (gridId: string): Promise<GridSummary | null> => {
  const snap = await getDoc(doc(db, 'gridSummaries', gridId));
  if (!snap.exists()) return null;
  return { id: snap.id, ...snap.data() } as GridSummary;
};

// ============================================================================
// Quote Bank CRUD
// ============================================================================

export const fetchQuoteBankItems = async (projectId: string, orgId: string): Promise<QuoteBankItem[]> => {
  const snap = await getDocs(
    query(collection(db, 'quoteBankItems'), where('projectId', '==', projectId), where('organizationId', '==', orgId))
  );
  return snap.docs.map(d => ({ id: d.id, ...d.data() } as QuoteBankItem));
};

export const createQuoteBankItem = async (
  item: Omit<QuoteBankItem, 'id' | 'createdAt' | 'updatedAt'>
): Promise<QuoteBankItem> => {
  const now = new Date().toISOString();
  const ref = doc(collection(db, 'quoteBankItems'));
  const data = { ...item, createdAt: now, updatedAt: now };
  await setDoc(ref, data);
  return { id: ref.id, ...data };
};

export const updateQuoteBankItem = async (
  itemId: string,
  updates: Partial<Pick<QuoteBankItem, 'text' | 'tags' | 'note' | 'starred'>>
): Promise<void> => {
  await updateDoc(doc(db, 'quoteBankItems', itemId), {
    ...updates,
    updatedAt: new Date().toISOString(),
  });
};

export const deleteQuoteBankItem = async (itemId: string): Promise<void> => {
  await deleteDoc(doc(db, 'quoteBankItems', itemId));
};

// ============================================================================
// AI Reports CRUD
// ============================================================================

export const fetchReports = async (projectId: string, orgId: string): Promise<AIReport[]> => {
  const snap = await getDocs(
    query(collection(db, 'reports'), where('projectId', '==', projectId), where('organizationId', '==', orgId))
  );
  return snap.docs.map(d => ({ id: d.id, ...d.data() } as AIReport));
};

export const fetchReport = async (reportId: string): Promise<AIReport | null> => {
  const snap = await getDoc(doc(db, 'reports', reportId));
  if (!snap.exists()) return null;
  return { id: snap.id, ...snap.data() } as AIReport;
};

export const updateReportBlock = async (
  reportId: string,
  blockId: string,
  updates: Record<string, unknown>,
): Promise<void> => {
  const reportSnap = await getDoc(doc(db, 'reports', reportId));
  if (!reportSnap.exists()) return;
  const report = reportSnap.data() as AIReport;
  if (!report.blocks) return;
  const updatedBlocks = report.blocks.map(b =>
    b.id === blockId ? { ...b, ...updates } : b
  );
  await updateDoc(doc(db, 'reports', reportId), {
    blocks: updatedBlocks,
    lastEditedAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  });
};

export const deleteReport = async (reportId: string): Promise<void> => {
  await deleteDoc(doc(db, 'reports', reportId));
};

export const updateReportStoryMedia = async (
  reportId: string,
  storyMedia: AIReport['storyMedia'],
): Promise<void> => {
  await updateDoc(doc(db, 'reports', reportId), {
    storyMedia,
    updatedAt: new Date().toISOString(),
  });
};

// ============================================================================
// Shared Reports CRUD
// ============================================================================

export const fetchSharedReports = async (projectId: string): Promise<SharedReport[]> => {
  const snap = await getDocs(
    query(collection(db, 'sharedReports'), where('projectId', '==', projectId))
  );
  return snap.docs.map(d => ({ id: d.id, ...d.data() } as SharedReport));
};

export const fetchActiveShareLink = async (reportId: string): Promise<SharedReport | null> => {
  const snap = await getDocs(
    query(
      collection(db, 'sharedReports'),
      where('reportId', '==', reportId),
      where('isActive', '==', true)
    )
  );
  if (snap.empty) return null;
  return { id: snap.docs[0].id, ...snap.docs[0].data() } as SharedReport;
};
